import React, { useCallback } from 'react';
import {
  FaMicrophone,
  FaMicrophoneSlash,
  FaSpinner,
  FaVideo,
  FaVideoSlash
} from 'react-icons/fa';
import { IconButton } from '../common/IconButton.js';
import { useVideoCall } from '../../hooks/useVideoCall.js';

interface MediaToggleButtonProps {
  /** Stable name of the device. The label must not change with the state — `aria-pressed` carries that. */
  label: string;
  /** What the device is really doing, as reported by LiveKit. */
  enabled: boolean;
  /** A `setXEnabled` call is in flight. */
  pending: boolean;
  /** Media controls are unavailable (Busy / Do Not Disturb). */
  locked: boolean;
  onToggle: () => void;
  enabledIcon: React.ReactNode;
  disabledIcon: React.ReactNode;
  testId: string;
}

/**
 * One device button. Everything it shows — colour, icon, pressed state — comes
 * from `enabled`, which is the published track state, never a stored
 * preference: a button that says "live" while nothing is published is the worst
 * failure this tool has (#75).
 */
const MediaToggleButton: React.FC<MediaToggleButtonProps> = ({
  label,
  enabled,
  pending,
  locked,
  onToggle,
  enabledIcon,
  disabledIcon,
  testId
}) => {
  const tooltip = locked
    ? `${label} unavailable in this status`
    : `${enabled ? 'Turn off' : 'Turn on'} ${label.toLowerCase()}`;

  return (
    <div className="relative group">
      <IconButton
        icon={
          pending ? (
            <FaSpinner
              className="animate-spin text-white"
              data-testid={`${testId}-spinner`}
              aria-hidden="true"
            />
          ) : enabled ? (
            enabledIcon
          ) : (
            disabledIcon
          )
        }
        onClick={onToggle}
        ariaLabel={label}
        aria-pressed={enabled}
        aria-busy={pending}
        disabled={locked || pending}
        data-testid={testId}
        className={`h-10 px-3 transition-all ${
          enabled
            ? 'bg-game-teal hover:bg-game-light border border-game-teal/50'
            : 'bg-game-orange hover:bg-game-red border border-game-orange/50'
        }`}
      />
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        {tooltip}
      </div>
    </div>
  );
};

/**
 * The microphone and camera buttons.
 *
 * They are driven by `VideoContext`, which subscribes to the LiveKit room's
 * own track events, so they report what is actually being published rather than
 * what localStorage remembers. The stored preference survives only as the value
 * the *next* connection will ask for.
 */
export function MediaToggleControls({ className = '' }: { className?: string }) {
  const {
    micEnabled,
    cameraEnabled,
    micPending,
    cameraPending,
    canControl,
    toggleMic,
    toggleCamera
  } = useVideoCall();

  // The context returns promises; a click handler should not.
  const handleToggleMic = useCallback(() => {
    void toggleMic();
  }, [toggleMic]);

  const handleToggleCamera = useCallback(() => {
    void toggleCamera();
  }, [toggleCamera]);

  return (
    <div className={`flex items-center gap-1 ${className}`.trim()}>
      <MediaToggleButton
        label="Microphone"
        enabled={micEnabled}
        pending={micPending}
        locked={!canControl}
        onToggle={handleToggleMic}
        enabledIcon={<FaMicrophone className="text-game-dark" />}
        disabledIcon={<FaMicrophoneSlash className="text-white" />}
        testId="mic-toggle"
      />
      <MediaToggleButton
        label="Camera"
        enabled={cameraEnabled}
        pending={cameraPending}
        locked={!canControl}
        onToggle={handleToggleCamera}
        enabledIcon={<FaVideo className="text-game-dark" />}
        disabledIcon={<FaVideoSlash className="text-white" />}
        testId="camera-toggle"
      />
    </div>
  );
}
